import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Plus, Calendar as CalendarIcon, MapPin, Users, Download, Trash2,
  Edit3, LogOut, Settings as SettingsIcon, ListChecks, Anchor, Sailboat, Image as ImageIcon,
  FormInput, GripVertical, ChevronUp, ChevronDown
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, API, formatApiError } from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

function blankEvent() {
  return { title: "", date: "", time: "", location: "", capacity: 30, description: "" };
}

function formatPrettyDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
      weekday: "short", month: "short", day: "numeric", year: "numeric",
    });
  } catch { return iso; }
}

function formatPrettyTime(t) {
  if (!t) return "";
  const [hh, mm] = t.split(":");
  const h = parseInt(hh, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${mm} ${ampm}`;
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [settings, setSettings] = useState({ banner_image: "", welcome_message: "", event_details: "", cc_emails: "", reply_to_email: "", form_labels: {}, custom_fields: [] });

  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState(blankEvent());
  const [savingEvent, setSavingEvent] = useState(false);

  const [savingSettings, setSavingSettings] = useState(false);

  const handleLogout = () => {
    logout();
    toast.success("Signed out");
    navigate("/admin/login");
  };

  const loadEvents = async () => {
    const res = await api.get("/events");
    setEvents(res.data);
  };

  const loadInquiries = async (eventId) => {
    const res = await api.get("/admin/inquiries", { params: eventId ? { event_id: eventId } : {} });
    setInquiries(res.data);
  };

  const loadSettings = async () => {
    const res = await api.get("/settings");
    setSettings(res.data || {});
  };

  useEffect(() => {
    Promise.all([loadEvents(), loadSettings()]).catch(() => toast.error("Couldn't load data"));
  }, []);

  useEffect(() => {
    if (selectedEventId === "") return;
    loadInquiries(selectedEventId === "all" ? "" : selectedEventId).catch(() =>
      toast.error("Couldn't load RSVPs")
    );
  }, [selectedEventId]);

  const openCreateEvent = () => {
    setEditingEvent(null);
    setEventForm(blankEvent());
    setEventDialogOpen(true);
  };

  const openEditEvent = (ev) => {
    setEditingEvent(ev);
    setEventForm({
      title: ev.title, date: ev.date, time: ev.time,
      location: ev.location, capacity: ev.capacity, description: ev.description || "",
    });
    setEventDialogOpen(true);
  };

  const saveEvent = async () => {
    if (!eventForm.title.trim() || !eventForm.date || !eventForm.time || !eventForm.location.trim()) {
      toast.error("Please fill in title, date, time, and location.");
      return;
    }
    setSavingEvent(true);
    try {
      if (editingEvent) {
        await api.put(`/admin/events/${editingEvent.id}`, {
          ...eventForm, capacity: Number(eventForm.capacity) || 1,
        });
        toast.success("Event updated");
      } else {
        await api.post("/admin/events", {
          ...eventForm, capacity: Number(eventForm.capacity) || 1,
        });
        toast.success("Event created");
      }
      setEventDialogOpen(false);
      await loadEvents();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingEvent(false);
    }
  };

  const deleteEvent = async (id) => {
    try {
      await api.delete(`/admin/events/${id}`);
      toast.success("Event deleted");
      await loadEvents();
      if (selectedEventId === id) setSelectedEventId("");
    } catch (err) {
      toast.error("Couldn't delete event");
    }
  };

  const deleteInquiry = async (id) => {
    try {
      await api.delete(`/admin/inquiries/${id}`);
      toast.success("RSVP removed");
      await loadInquiries(selectedEventId === "all" ? "" : selectedEventId);
      await loadEvents();
    } catch {
      toast.error("Couldn't remove RSVP");
    }
  };

  const downloadCSV = async (eventId) => {
    const ev = events.find((e) => e.id === eventId);
    if (!ev) return;
    const token = localStorage.getItem("rr_admin_token");
    const url = `${API}/admin/events/${eventId}/report.csv?token=${encodeURIComponent(token)}`;
    // Use fetch to inspect headers, then trigger download
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${ev.title.replace(/[^a-z0-9]/gi, "_")}_${ev.date}_report.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Report downloaded");
    } catch {
      toast.error("Could not download CSV");
    }
  };

  const onBannerFile = (file) => {
    const reader = new FileReader();
    reader.onload = () => setSettings((s) => ({ ...s, banner_image: reader.result }));
    reader.readAsDataURL(file);
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await api.put("/settings", {
        banner_image: settings.banner_image || "",
        welcome_message: settings.welcome_message || "",
        event_details: settings.event_details || "",
        cc_emails: settings.cc_emails || "",
        reply_to_email: settings.reply_to_email || "",
      });
      setSettings((s) => ({ ...s, ...res.data }));
      toast.success("Settings saved");
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const [savingForm, setSavingForm] = useState(false);
  const saveFormEditor = async () => {
    setSavingForm(true);
    try {
      const res = await api.put("/settings", {
        form_labels: settings.form_labels || {},
        custom_fields: settings.custom_fields || [],
      });
      setSettings((s) => ({ ...s, ...res.data }));
      toast.success("Form saved");
    } catch {
      toast.error("Could not save form");
    } finally {
      setSavingForm(false);
    }
  };

  const updateLabel = (key, value) =>
    setSettings((s) => ({ ...s, form_labels: { ...(s.form_labels || {}), [key]: value } }));

  const addCustomField = (type) => {
    const id = "cf_" + Math.random().toString(36).slice(2, 10);
    const base = { id, type, label: "New question", required: false };
    const field = type === "select"
      ? { ...base, options: ["Option 1", "Option 2"] }
      : type === "checkbox"
      ? { ...base, label: "I agree to..." }
      : { ...base, placeholder: "" };
    setSettings((s) => ({ ...s, custom_fields: [ ...(s.custom_fields || []), field ] }));
  };

  const updateCustomField = (id, patch) => {
    setSettings((s) => ({
      ...s,
      custom_fields: (s.custom_fields || []).map((f) => (f.id === id ? { ...f, ...patch } : f)),
    }));
  };

  const removeCustomField = (id) => {
    setSettings((s) => ({ ...s, custom_fields: (s.custom_fields || []).filter((f) => f.id !== id) }));
  };

  const moveCustomField = (id, dir) => {
    setSettings((s) => {
      const list = [...(s.custom_fields || [])];
      const i = list.findIndex((f) => f.id === id);
      if (i < 0) return s;
      const j = i + dir;
      if (j < 0 || j >= list.length) return s;
      [list[i], list[j]] = [list[j], list[i]];
      return { ...s, custom_fields: list };
    });
  };

  const totals = useMemo(() => {
    const totalRsvps = events.reduce((s, e) => s + (e.registered_count || 0), 0);
    const totalCapacity = events.reduce((s, e) => s + e.capacity, 0);
    return { totalRsvps, totalCapacity };
  }, [events]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Top bar */}
      <header className="bg-white border-b border-blue-50 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Rolling River" className="h-12 w-12 object-contain" />
            <div>
              <div className="font-display font-bold text-[#1E3A8A] text-lg leading-none">Admin</div>
              <div className="text-xs text-slate-500">Prospective Family Days Console</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline text-sm text-slate-600">
              {user?.email}
            </span>
            <Button
              data-testid="logout-button"
              variant="outline"
              onClick={handleLogout}
              className="rounded-full border-2 border-blue-100 text-[#1E3A8A] hover:bg-blue-50"
            >
              <LogOut className="h-4 w-4 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stat cards */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <StatCard
            icon={<CalendarIcon className="h-5 w-5" />}
            label="Total Events"
            value={events.length}
            tone="blue"
            testid="stat-events"
          />
          <StatCard
            icon={<Users className="h-5 w-5" />}
            label="Total RSVPs"
            value={totals.totalRsvps}
            tone="yellow"
            testid="stat-rsvps"
          />
          <StatCard
            icon={<Sailboat className="h-5 w-5" />}
            label="Total Capacity"
            value={totals.totalCapacity}
            tone="sky"
            testid="stat-capacity"
          />
        </div>

        <Tabs defaultValue="events" className="w-full">
          <TabsList
            className="bg-white border border-blue-50 rounded-full p-1 h-12 mb-6"
            data-testid="admin-tabs"
          >
            <TabsTrigger
              value="events"
              className="rounded-full px-5 data-[state=active]:bg-[#2F5FCB] data-[state=active]:text-white font-bold"
              data-testid="tab-events"
            >
              <CalendarIcon className="h-4 w-4 mr-1.5" /> Events
            </TabsTrigger>
            <TabsTrigger
              value="rsvps"
              className="rounded-full px-5 data-[state=active]:bg-[#2F5FCB] data-[state=active]:text-white font-bold"
              data-testid="tab-rsvps"
            >
              <ListChecks className="h-4 w-4 mr-1.5" /> RSVPs
            </TabsTrigger>
            <TabsTrigger
              value="form"
              className="rounded-full px-5 data-[state=active]:bg-[#2F5FCB] data-[state=active]:text-white font-bold"
              data-testid="tab-form"
            >
              <FormInput className="h-4 w-4 mr-1.5" /> Form Editor
            </TabsTrigger>
            <TabsTrigger
              value="settings"
              className="rounded-full px-5 data-[state=active]:bg-[#2F5FCB] data-[state=active]:text-white font-bold"
              data-testid="tab-settings"
            >
              <SettingsIcon className="h-4 w-4 mr-1.5" /> Settings
            </TabsTrigger>
          </TabsList>

          {/* EVENTS */}
          <TabsContent value="events">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
              <div>
                <h2 className="font-display text-2xl font-bold text-[#1E3A8A]">Manage Events</h2>
                <p className="text-sm text-slate-500">Add and edit Prospective Family Day dates</p>
              </div>
              <Button
                data-testid="add-event-button"
                onClick={openCreateEvent}
                className="bg-[#2F5FCB] hover:bg-[#254BA0] text-white rounded-full px-6 py-5 font-bold hover:-translate-y-0.5 transition-all"
              >
                <Plus className="h-4 w-4 mr-1.5" /> Add Event
              </Button>
            </div>

            <div className="bg-white rounded-3xl border border-blue-50 shadow-sm overflow-hidden">
              <Table data-testid="events-table">
                <TableHeader>
                  <TableRow className="bg-blue-50/50">
                    <TableHead className="font-bold text-[#1E3A8A]">Title</TableHead>
                    <TableHead className="font-bold text-[#1E3A8A]">Date & Time</TableHead>
                    <TableHead className="font-bold text-[#1E3A8A]">Location</TableHead>
                    <TableHead className="font-bold text-[#1E3A8A]">RSVPs / Capacity</TableHead>
                    <TableHead className="text-right font-bold text-[#1E3A8A]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                        No events yet. Click <strong>Add Event</strong> to create your first one.
                      </TableCell>
                    </TableRow>
                  )}
                  {events.map((ev) => (
                    <TableRow key={ev.id} data-testid={`event-row-${ev.id}`}>
                      <TableCell className="font-bold text-[#1E3A8A]">{ev.title}</TableCell>
                      <TableCell>
                        <div className="font-medium">{formatPrettyDate(ev.date)}</div>
                        <div className="text-xs text-slate-500">{formatPrettyTime(ev.time)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 text-slate-700">
                          <MapPin className="h-3.5 w-3.5" /> {ev.location}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1 bg-blue-50 text-[#1E3A8A] px-3 py-1 rounded-full font-bold text-sm">
                          {ev.registered_count || 0} / {ev.capacity}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-1">
                          <Button
                            data-testid={`download-csv-${ev.id}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => downloadCSV(ev.id)}
                            className="rounded-full text-[#2F5FCB] hover:bg-blue-50"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            data-testid={`edit-event-${ev.id}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditEvent(ev)}
                            className="rounded-full text-[#1E3A8A] hover:bg-blue-50"
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                data-testid={`delete-event-${ev.id}`}
                                size="sm"
                                variant="ghost"
                                className="rounded-full text-red-500 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="rounded-3xl">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete event?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete <strong>{ev.title}</strong> and all of its RSVPs. This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  data-testid={`confirm-delete-event-${ev.id}`}
                                  onClick={() => deleteEvent(ev.id)}
                                  className="rounded-full bg-red-500 hover:bg-red-600"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* RSVPS */}
          <TabsContent value="rsvps">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
              <div>
                <h2 className="font-display text-2xl font-bold text-[#1E3A8A]">View RSVPs</h2>
                <p className="text-sm text-slate-500">See who's attending each event and download CSV reports</p>
              </div>
              <div className="flex items-center gap-2">
                <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                  <SelectTrigger
                    data-testid="rsvp-event-select"
                    className="w-72 rounded-full border-2 border-blue-100 bg-white font-medium"
                  >
                    <SelectValue placeholder="Select an event…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All events</SelectItem>
                    {events.map((ev) => (
                      <SelectItem key={ev.id} value={ev.id}>
                        {ev.title} · {formatPrettyDate(ev.date)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedEventId && selectedEventId !== "all" && (
                  <Button
                    data-testid="download-csv-button"
                    onClick={() => downloadCSV(selectedEventId)}
                    className="bg-yellow-300 hover:bg-yellow-400 text-[#1E3A8A] rounded-full px-6 font-bold"
                  >
                    <Download className="h-4 w-4 mr-1.5" /> Download CSV
                  </Button>
                )}
              </div>
            </div>

            <div className="bg-white rounded-3xl border border-blue-50 shadow-sm overflow-hidden">
              <Table data-testid="rsvps-table">
                <TableHeader>
                  <TableRow className="bg-blue-50/50">
                    <TableHead className="font-bold text-[#1E3A8A]">Submitted</TableHead>
                    <TableHead className="font-bold text-[#1E3A8A]">Parent(s)</TableHead>
                    <TableHead className="font-bold text-[#1E3A8A]">Contact</TableHead>
                    <TableHead className="font-bold text-[#1E3A8A]">Children</TableHead>
                    <TableHead className="font-bold text-[#1E3A8A]">Adults</TableHead>
                    {selectedEventId === "all" && (
                      <TableHead className="font-bold text-[#1E3A8A]">Event</TableHead>
                    )}
                    <TableHead className="text-right font-bold text-[#1E3A8A]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!selectedEventId && (
                    <TableRow>
                      <TableCell colSpan={selectedEventId === "all" ? 7 : 6} className="text-center py-12 text-slate-500">
                        Pick an event above to view its attendees.
                      </TableCell>
                    </TableRow>
                  )}
                  {selectedEventId && inquiries.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={selectedEventId === "all" ? 7 : 6} className="text-center py-12 text-slate-500">
                        No RSVPs for this event yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {inquiries.map((inq) => {
                    const ev = events.find((e) => e.id === inq.event_id);
                    return (
                      <TableRow key={inq.id} data-testid={`rsvp-row-${inq.id}`}>
                        <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                          {new Date(inq.submitted_at).toLocaleString()}
                        </TableCell>
                        <TableCell className="font-bold text-[#1E3A8A]">{inq.parent_names}</TableCell>
                        <TableCell>
                          <div className="text-sm">{inq.email || <span className="text-slate-400">—</span>}</div>
                          <div className="text-xs text-slate-500">{inq.phone}</div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {inq.children.map((c, i) => (
                            <div key={i} className="leading-tight">
                              <strong>{c.name}</strong>{" "}
                              <span className="text-slate-500">· {c.age} · {c.grade}</span>
                            </div>
                          ))}
                        </TableCell>
                        <TableCell className="text-sm">
                          <div className="font-bold">{inq.adult_count}</div>
                          {inq.adults?.length > 0 && (
                            <div className="text-xs text-slate-500">
                              {inq.adults.map((a) => a.name).filter(Boolean).join(", ")}
                            </div>
                          )}
                        </TableCell>
                        {selectedEventId === "all" && (
                          <TableCell className="text-xs">
                            {ev ? <>{ev.title}<br /><span className="text-slate-500">{formatPrettyDate(ev.date)}</span></> : "—"}
                          </TableCell>
                        )}
                        <TableCell className="text-right">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                data-testid={`delete-rsvp-${inq.id}`}
                                size="sm"
                                variant="ghost"
                                className="rounded-full text-red-500 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="rounded-3xl">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remove RSVP?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will remove the RSVP from <strong>{inq.parent_names}</strong>.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deleteInquiry(inq.id)}
                                  className="rounded-full bg-red-500 hover:bg-red-600"
                                >
                                  Remove
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* FORM EDITOR */}
          <TabsContent value="form">
            <div className="bg-white rounded-3xl border border-blue-50 shadow-sm p-6 md:p-10 max-w-4xl">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="font-display text-2xl font-bold text-[#1E3A8A]">Form Editor</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    Edit any label on the public RSVP form, and add your own custom questions.
                  </p>
                </div>
                <Button
                  data-testid="save-form-button"
                  onClick={saveFormEditor}
                  disabled={savingForm}
                  className="bg-[#2F5FCB] hover:bg-[#254BA0] text-white rounded-full px-7 py-5 font-bold"
                >
                  {savingForm ? "Saving…" : "Save form"}
                  <Anchor className="h-4 w-4 ml-2" />
                </Button>
              </div>

              {/* Labels */}
              <div className="mt-8">
                <h3 className="font-display text-lg font-bold text-[#1E3A8A]">Labels & text</h3>
                <p className="text-xs text-slate-500 mb-4">Edit any of the text shown on the form.</p>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    ["form_title", "Form heading"],
                    ["parent_names_label", "Parent name(s) label"],
                    ["parent_names_placeholder", "Parent name(s) placeholder"],
                    ["email_label", "Email label"],
                    ["email_placeholder", "Email placeholder"],
                    ["phone_label", "Phone label"],
                    ["phone_placeholder", "Phone placeholder"],
                    ["children_section_title", "Children section title"],
                    ["add_child_button", "\"Add another child\" button text"],
                    ["child_name_placeholder", "Child name placeholder"],
                    ["child_age_placeholder", "Child age placeholder"],
                    ["child_grade_placeholder", "Child grade placeholder"],
                    ["adults_section_title", "Adults section title"],
                    ["adults_section_subtitle", "Adults section subtitle"],
                    ["adult_name_placeholder", "Adult name placeholder"],
                    ["notes_label", "Notes field label"],
                    ["notes_placeholder", "Notes field placeholder"],
                    ["sms_consent_title", "SMS opt-out title"],
                    ["sms_consent_body", "SMS opt-out body"],
                    ["submit_button", "Submit button text"],
                    ["form_footer_note", "Fine print under submit button"],
                    ["success_heading", "Success screen heading"],
                    ["success_body", "Success screen body"],
                  ].map(([key, human]) => {
                    const isLong = key.includes("body") || key.includes("footer") || key.includes("consent_body");
                    const val = (settings.form_labels || {})[key] || "";
                    return (
                      <div key={key} className={`space-y-1 ${isLong ? "md:col-span-2" : ""}`}>
                        <Label className="text-xs font-bold text-[#1E3A8A]">{human}</Label>
                        {isLong ? (
                          <Textarea
                            data-testid={`label-${key}`}
                            value={val}
                            onChange={(e) => updateLabel(key, e.target.value)}
                            className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] min-h-[70px] text-sm"
                          />
                        ) : (
                          <Input
                            data-testid={`label-${key}`}
                            value={val}
                            onChange={(e) => updateLabel(key, e.target.value)}
                            className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] py-2.5 text-sm"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Custom fields */}
              <div className="mt-10 pt-8 border-t border-blue-50">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-display text-lg font-bold text-[#1E3A8A]">Custom questions</h3>
                    <p className="text-xs text-slate-500">Add extra questions to the form. Answers appear in the CSV report.</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button data-testid="add-custom-text" onClick={() => addCustomField("text")} size="sm" className="rounded-full bg-[#2F5FCB] hover:bg-[#254BA0] text-white font-bold">
                      <Plus className="h-3.5 w-3.5 mr-1" /> Short text
                    </Button>
                    <Button data-testid="add-custom-textarea" onClick={() => addCustomField("textarea")} size="sm" className="rounded-full bg-[#2F5FCB] hover:bg-[#254BA0] text-white font-bold">
                      <Plus className="h-3.5 w-3.5 mr-1" /> Long text
                    </Button>
                    <Button data-testid="add-custom-select" onClick={() => addCustomField("select")} size="sm" className="rounded-full bg-[#2F5FCB] hover:bg-[#254BA0] text-white font-bold">
                      <Plus className="h-3.5 w-3.5 mr-1" /> Dropdown
                    </Button>
                    <Button data-testid="add-custom-checkbox" onClick={() => addCustomField("checkbox")} size="sm" className="rounded-full bg-[#2F5FCB] hover:bg-[#254BA0] text-white font-bold">
                      <Plus className="h-3.5 w-3.5 mr-1" /> Checkbox
                    </Button>
                  </div>
                </div>

                <div className="mt-5 space-y-4">
                  {(settings.custom_fields || []).length === 0 && (
                    <div className="text-center py-10 bg-blue-50/40 rounded-2xl border-2 border-dashed border-blue-200 text-slate-500 text-sm">
                      No custom questions yet. Click one of the buttons above to add one.
                    </div>
                  )}
                  {(settings.custom_fields || []).map((f, idx) => (
                    <div key={f.id} data-testid={`custom-field-${f.id}`} className="bg-blue-50/40 rounded-2xl p-5 border border-blue-100">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs font-bold text-white bg-[#2F5FCB] rounded-full px-2 py-0.5 uppercase tracking-wider">{f.type}</span>
                        <span className="text-xs text-slate-500">Question {idx + 1}</span>
                        <div className="ml-auto flex items-center gap-1">
                          <button type="button" onClick={() => moveCustomField(f.id, -1)} className="p-1.5 rounded-full hover:bg-white text-slate-500 disabled:opacity-30" disabled={idx === 0}>
                            <ChevronUp className="h-4 w-4" />
                          </button>
                          <button type="button" onClick={() => moveCustomField(f.id, 1)} className="p-1.5 rounded-full hover:bg-white text-slate-500 disabled:opacity-30" disabled={idx === (settings.custom_fields || []).length - 1}>
                            <ChevronDown className="h-4 w-4" />
                          </button>
                          <button type="button" data-testid={`remove-custom-${f.id}`} onClick={() => removeCustomField(f.id)} className="p-1.5 rounded-full hover:bg-red-50 text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div className="space-y-1 md:col-span-2">
                          <Label className="text-xs font-bold text-[#1E3A8A]">Question / label</Label>
                          <Input
                            data-testid={`custom-label-${f.id}`}
                            value={f.label || ""}
                            onChange={(e) => updateCustomField(f.id, { label: e.target.value })}
                            className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] py-2.5 bg-white text-sm"
                          />
                        </div>
                        {(f.type === "text" || f.type === "textarea") && (
                          <div className="space-y-1 md:col-span-2">
                            <Label className="text-xs font-bold text-[#1E3A8A]">Placeholder (optional)</Label>
                            <Input
                              value={f.placeholder || ""}
                              onChange={(e) => updateCustomField(f.id, { placeholder: e.target.value })}
                              className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] py-2.5 bg-white text-sm"
                            />
                          </div>
                        )}
                        {f.type === "select" && (
                          <div className="space-y-1 md:col-span-2">
                            <Label className="text-xs font-bold text-[#1E3A8A]">Options (one per line)</Label>
                            <Textarea
                              value={(f.options || []).join("\n")}
                              onChange={(e) => updateCustomField(f.id, { options: e.target.value.split("\n").map((s) => s).filter((s) => s.trim() !== "") })}
                              placeholder={"Option 1\nOption 2\nOption 3"}
                              className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] min-h-[100px] bg-white text-sm"
                            />
                          </div>
                        )}
                        <div className="flex items-center gap-2 pt-1">
                          <input
                            type="checkbox"
                            id={`required-${f.id}`}
                            checked={Boolean(f.required)}
                            onChange={(e) => updateCustomField(f.id, { required: e.target.checked })}
                            className="h-4 w-4 rounded"
                          />
                          <label htmlFor={`required-${f.id}`} className="text-sm text-slate-700 cursor-pointer">
                            Required
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 flex justify-end">
                <Button
                  data-testid="save-form-button-bottom"
                  onClick={saveFormEditor}
                  disabled={savingForm}
                  className="bg-[#2F5FCB] hover:bg-[#254BA0] text-white rounded-full px-7 py-5 font-bold"
                >
                  {savingForm ? "Saving…" : "Save form"}
                  <Anchor className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* SETTINGS */}
          <TabsContent value="settings">
            <div className="bg-white rounded-3xl border border-blue-50 shadow-sm p-6 md:p-10 max-w-3xl">
              <h2 className="font-display text-2xl font-bold text-[#1E3A8A]">Page Settings</h2>
              <p className="text-sm text-slate-500 mt-1">Customize the public RSVP page</p>

              <div className="mt-6 space-y-2">
                <Label className="text-sm font-bold text-[#1E3A8A]">Welcome message</Label>
                <Textarea
                  data-testid="settings-welcome-input"
                  value={settings.welcome_message || ""}
                  onChange={(e) => setSettings((s) => ({ ...s, welcome_message: e.target.value }))}
                  placeholder="Shown on the public hero section"
                  className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] min-h-[100px]"
                />
              </div>

              <div className="mt-6 space-y-2">
                <Label className="text-sm font-bold text-[#1E3A8A]">Event details (shown in "Event Details" dialog)</Label>
                <p className="text-xs text-slate-500">
                  This text reappears every year on the public page when a parent clicks "Event Details" on any event card. Update it as the season changes.
                </p>
                <Textarea
                  data-testid="settings-event-details-input"
                  value={settings.event_details || ""}
                  onChange={(e) => setSettings((s) => ({ ...s, event_details: e.target.value }))}
                  placeholder="Overview, what's included, history, fine print…"
                  className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] min-h-[300px] font-mono text-sm leading-relaxed"
                />
              </div>

              <div className="mt-8">
                <Label className="text-sm font-bold text-[#1E3A8A] block mb-2">Banner image</Label>
                <p className="text-xs text-slate-500 mb-3">
                  Upload an image file or paste an external URL. Recommended: 1400×900 landscape.
                </p>

                <div className="flex flex-wrap items-start gap-4">
                  <div className="relative bg-blue-50 rounded-2xl overflow-hidden w-64 h-40 flex items-center justify-center">
                    {settings.banner_image ? (
                      <img
                        src={settings.banner_image}
                        alt="Banner preview"
                        className="w-full h-full object-cover"
                        data-testid="banner-preview"
                      />
                    ) : (
                      <div className="text-slate-400 flex flex-col items-center text-xs">
                        <ImageIcon className="h-8 w-8 mb-1" />
                        No banner set (default shown)
                      </div>
                    )}
                  </div>

                  <div className="flex-1 space-y-3 min-w-[260px]">
                    <div>
                      <Label className="text-xs text-slate-500">Upload from your computer</Label>
                      <input
                        data-testid="banner-upload-input"
                        type="file"
                        accept="image/*"
                        onChange={(e) => e.target.files?.[0] && onBannerFile(e.target.files[0])}
                        className="mt-1 block w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:font-bold file:bg-[#2F5FCB] file:text-white hover:file:bg-[#254BA0]"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Or paste image URL</Label>
                      <Input
                        data-testid="banner-url-input"
                        value={settings.banner_image?.startsWith("data:") ? "" : (settings.banner_image || "")}
                        onChange={(e) => setSettings((s) => ({ ...s, banner_image: e.target.value }))}
                        placeholder="https://…"
                        className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] py-3"
                      />
                    </div>
                    {settings.banner_image && (
                      <button
                        type="button"
                        data-testid="banner-clear"
                        onClick={() => setSettings((s) => ({ ...s, banner_image: "" }))}
                        className="text-xs text-red-500 font-bold hover:underline"
                      >
                        Clear banner (use default)
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-blue-50">
                <h3 className="font-display text-xl font-bold text-[#1E3A8A]">Email confirmations</h3>
                <p className="text-sm text-slate-500 mt-1">
                  When a family submits an RSVP, we'll send them a confirmation email with a calendar invite attached.
                </p>

                <div className="mt-5 space-y-2">
                  <Label className="text-sm font-bold text-[#1E3A8A]">CC emails</Label>
                  <p className="text-xs text-slate-500">
                    Anyone here will get a copy of every confirmation. Separate multiple addresses with commas — e.g. <em>info@rollingriver.com, director@rollingriver.com</em>
                  </p>
                  <Input
                    data-testid="settings-cc-emails-input"
                    value={settings.cc_emails || ""}
                    onChange={(e) => setSettings((s) => ({ ...s, cc_emails: e.target.value }))}
                    placeholder="info@rollingriver.com, director@rollingriver.com"
                    className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] py-3"
                  />
                </div>

                <div className="mt-5 space-y-2">
                  <Label className="text-sm font-bold text-[#1E3A8A]">Reply-To email</Label>
                  <p className="text-xs text-slate-500">
                    Replies from parents go here. This is independent from the verified Resend "from" address — set this to wherever you actually read mail (e.g. <em>info@rollingriver.com</em>).
                  </p>
                  <Input
                    data-testid="settings-reply-to-input"
                    type="email"
                    value={settings.reply_to_email || ""}
                    onChange={(e) => setSettings((s) => ({ ...s, reply_to_email: e.target.value }))}
                    placeholder="info@rollingriver.com"
                    className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] py-3"
                  />
                </div>
              </div>

              <div className="mt-8">
                <Button
                  data-testid="save-settings-button"
                  onClick={saveSettings}
                  disabled={savingSettings}
                  className="bg-[#2F5FCB] hover:bg-[#254BA0] text-white rounded-full px-7 py-5 font-bold"
                >
                  {savingSettings ? "Saving…" : "Save settings"}
                  <Anchor className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Event Create/Edit Dialog */}
      <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
        <DialogContent className="rounded-3xl max-w-lg" data-testid="event-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1E3A8A]">
              {editingEvent ? "Edit event" : "Add new event"}
            </DialogTitle>
            <DialogDescription>
              {editingEvent ? "Update the event details below." : "Set up a Prospective Family Day event."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-bold text-[#1E3A8A]">Title *</Label>
              <Input
                data-testid="event-form-title"
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                placeholder="e.g. Spring Open House"
                className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] py-3"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm font-bold text-[#1E3A8A]">Date *</Label>
                <Input
                  data-testid="event-form-date"
                  type="date"
                  value={eventForm.date}
                  onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                  className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] py-3"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-bold text-[#1E3A8A]">Time *</Label>
                <Input
                  data-testid="event-form-time"
                  type="time"
                  value={eventForm.time}
                  onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
                  className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] py-3"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-bold text-[#1E3A8A]">Location *</Label>
              <Input
                data-testid="event-form-location"
                value={eventForm.location}
                onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                placeholder="e.g. Rolling River Main Camp, 123 Camp Rd"
                className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] py-3"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-bold text-[#1E3A8A]">Capacity</Label>
              <Input
                data-testid="event-form-capacity"
                type="number"
                min="1"
                value={eventForm.capacity}
                onChange={(e) => setEventForm({ ...eventForm, capacity: e.target.value })}
                className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] py-3"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-bold text-[#1E3A8A]">Description</Label>
              <Textarea
                data-testid="event-form-description"
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                placeholder="Optional details shown on the public page"
                className="rounded-xl border-2 border-blue-100 focus:border-[#2F5FCB] min-h-[80px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEventDialogOpen(false)}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              data-testid="event-form-submit"
              onClick={saveEvent}
              disabled={savingEvent}
              className="bg-[#2F5FCB] hover:bg-[#254BA0] text-white rounded-full px-6 font-bold"
            >
              {savingEvent ? "Saving…" : (editingEvent ? "Save changes" : "Create event")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon, label, value, tone, testid }) {
  const tones = {
    blue: "bg-[#2F5FCB] text-white",
    yellow: "bg-yellow-300 text-[#1E3A8A]",
    sky: "bg-sky-200 text-[#1E3A8A]",
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-3xl border border-blue-50 shadow-sm p-6 flex items-center justify-between"
      data-testid={testid}
    >
      <div>
        <div className="text-sm text-slate-500 font-medium">{label}</div>
        <div className="font-display text-3xl font-black text-[#1E3A8A] mt-1">{value}</div>
      </div>
      <div className={`h-12 w-12 rounded-2xl flex items-center justify-center ${tones[tone]}`}>
        {icon}
      </div>
    </motion.div>
  );
}
